import { useEffect, useRef } from 'react'
import { useLanguage } from '@context/LanguageContext'
import LanguageService from '@services/LanguageService'
import { getLocaleCode, getHreflangLinks, SUPPORTED_LANGUAGES } from '@constants/languages'
import { STATIC_PAGE_SEO } from '@config/seo'
import { LANGUAGES_WITH_LEGAL } from '@i18n/namespaces'
import { createStructuredData } from '@utils/structuredDataFactory'

export function useDocumentMetadata () {
  const { lang, tFunc, isReady } = useLanguage()
  const lastExecutionRef = useRef('')

  useEffect(() => {
    if (!lang || typeof tFunc !== 'function' || !isReady) return

    const currentPath = window.location.pathname
    const currentSignature = `${lang}-${currentPath}-${tFunc('meta.title')}`

    if (lastExecutionRef.current === currentSignature) {
      return
    }

    lastExecutionRef.current = currentSignature

    const getStaticPageType = () => {
      const path = window.location.pathname
      const staticPages = ['about', 'contact', 'support', 'privacy', 'terms', 'memorama', 'puzzle']
      const foundPage = staticPages.find(page => path.includes(`/${page}`))

      const supportedLangPattern = new RegExp(`^/(${SUPPORTED_LANGUAGES.join('|')})$`)
      if (!foundPage && path !== '/' && !path.match(supportedLangPattern)) {
        return 'not-found'
      }

      return foundPage || null
    }

    // Helper function to safely update meta tags
    const updateMetaTag = (selector, content) => {
      let metaTag = document.querySelector(selector)
      if (!metaTag) {
        // only create if it doesn't exist
        metaTag = document.createElement('meta')
        if (selector.includes('property=')) {
          const property = selector.match(/property="([^"]+)"/)[1]
          metaTag.setAttribute('property', property)
        } else if (selector.includes('name=')) {
          const name = selector.match(/name="([^"]+)"/)[1]
          metaTag.setAttribute('name', name)
        }
        document.head.appendChild(metaTag)
      }
      if (metaTag.content !== content) {
        metaTag.content = content
      }
    }

    const currentLang = LanguageService.getCurrentLanguageFromURL()

    const isRootOrDetection = window.location.pathname === '/'
    const effectiveLang = currentLang || (!isRootOrDetection ? lang : null)

    if (!effectiveLang) {
      document.title = 'Memodiez - Loading...'
      updateMetaTag('meta[name="description"]', 'Redirecting to preferred language...')
      updateMetaTag('meta[name="robots"]', 'noindex, follow')
      document.documentElement.lang = 'es' // Fallback to Spanish for redirection

      // Remove canonical for detection route (safety check)
      const existingCanonical = document.querySelector('link[rel="canonical"]')
      if (existingCanonical) {
        existingCanonical.remove()
      }

      // Remove hreflang for detection route (should not have any)
      const existingHreflang = document.querySelectorAll('link[hreflang]')
      if (existingHreflang.length > 0) {
        existingHreflang.forEach(link => link.remove())
      }

      return
    }

    // Detect current static page
    const currentPage = getStaticPageType()

    // Update <html lang="...">
    document.documentElement.lang = lang

    // Page specific metadata with fallback to general meta
    const pageTittle = currentPage && tFunc(`meta.pages.${currentPage}.title`)
      ? tFunc(`meta.pages.${currentPage}.title`)
      : tFunc('meta.title')

    const pageDescription = currentPage && tFunc(`meta.pages.${currentPage}.description`)
      ? tFunc(`meta.pages.${currentPage}.description`)
      : tFunc('meta.description')

    // basic metadata
    document.title = pageTittle
    updateMetaTag('meta[name="description"]', pageDescription)

    // Apply page specific robots directive
    const pageConfig = currentPage ? STATIC_PAGE_SEO[currentPage] : null
    const robotsContent = pageConfig ? pageConfig.robots : 'index, follow'
    updateMetaTag('meta[name="robots"]', robotsContent)

    const currentLocale = getLocaleCode(lang)

    // OpenGraph metadata (use page specific content)
    const ogTitle = (() => {
      if (!currentPage) return tFunc('meta.og.title')

      // Try specific OG title
      const specificOgTitle = tFunc(`meta.pages.${currentPage}.og.title`)
      if (specificOgTitle && !specificOgTitle.startsWith('[Missing:')) {
        return specificOgTitle
      }

      // Fallback to specific page title
      const specificTitle = tFunc(`meta.pages.${currentPage}.title`)
      if (specificTitle && !specificTitle.startsWith('[Missing:')) {
        return specificTitle
      }

      return tFunc('meta.og.title')
    })()

    const ogDescription = (() => {
      if (!currentPage) return tFunc('meta.og.description')

      // Try specific OG description
      const specificOgDescription = tFunc(`meta.pages.${currentPage}.og.description`)
      if (specificOgDescription && !specificOgDescription.startsWith('[Missing:')) {
        return specificOgDescription
      }

      // Fallback to specific page description
      const specificDescription = tFunc(`meta.pages.${currentPage}.description`)
      if (specificDescription && !specificDescription.startsWith('[Missing:')) {
        return specificDescription
      }

      return tFunc('meta.og.description')
    })()

    updateMetaTag('meta[property="og:title"]', ogTitle)
    updateMetaTag('meta[property="og:description"]', ogDescription)
    updateMetaTag('meta[property="og:locale"]', currentLocale)

    updateMetaTag('meta[property="og:image:alt"]', tFunc('meta.og.imageAlt'))

    // Twitter metadata (use page specific content)
    const twitterTitle = (() => {
      if (!currentPage) return tFunc('meta.twitter.title')

      // Try specific Twitter title
      const specificTwitterTitle = tFunc(`meta.pages.${currentPage}.twitter.title`)
      if (specificTwitterTitle && !specificTwitterTitle.startsWith('[Missing:')) {
        return specificTwitterTitle
      }

      const specificTitle = tFunc(`meta.pages.${currentPage}.title`)
      if (specificTitle && !specificTitle.startsWith('[Missing:')) {
        return specificTitle
      }

      return tFunc('meta.twitter.title')
    })()

    const twitterDescription = (() => {
      if (!currentPage) return tFunc('meta.twitter.description')

      // Try specific Twitter description
      const specificTwitterDescription = tFunc(`meta.pages.${currentPage}.twitter.description`)
      if (specificTwitterDescription && !specificTwitterDescription.startsWith('[Missing:')) {
        return specificTwitterDescription
      }

      // Fallback to specific page description
      const specificDescription = tFunc(`meta.pages.${currentPage}.description`)
      if (specificDescription && !specificDescription.startsWith('[Missing:')) {
        return specificDescription
      }

      return tFunc('meta.twitter.description')
    })()

    const twitterImageAlt = (() => {
      if (!currentPage) return tFunc('meta.twitter.imageAlt')

      // Try specific Twitter image alt
      const specificTwitterImageAlt = tFunc(`meta.pages.${currentPage}.twitter.imageAlt`)
      if (specificTwitterImageAlt && !specificTwitterImageAlt.startsWith('[Missing:')) {
        return specificTwitterImageAlt
      }

      return tFunc('meta.twitter.imageAlt')
    })()

    updateMetaTag('meta[name="twitter:title"]', twitterTitle)
    updateMetaTag('meta[name="twitter:description"]', twitterDescription)
    updateMetaTag('meta[name="twitter:image:alt"]', twitterImageAlt)

    // JSON-LD structured data (modifies existing script)
    const updateStructuredData = () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]')
      if (existingScript) {
        existingScript.remove()
      }

      const structuredData = createStructuredData({ currentPage, lang, tFunc })
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(structuredData, null, 2)
      document.head.appendChild(script)
    }

    updateStructuredData()

    const baseUrl = 'https://memodiez.com'
    const pagePath = currentPage ? `/${currentPage}` : ''
    const isLegalPage = ['privacy', 'terms'].includes(currentPage)

    // Update canonical URL based on current language route and page
    const updateCanonical = () => {
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.rel = 'canonical'
        document.head.appendChild(canonical)
      }

      // Legal pages with fallback: canonical points to fallabck language
      let canonicalLang = lang
      if (isLegalPage && !LANGUAGES_WITH_LEGAL.includes(lang)) {
        // de -> en, others -> es
        canonicalLang = lang === 'de' ? 'en' : 'es'
      }
      const newHref = `${baseUrl}/${canonicalLang}${pagePath}`
      if (canonical.href !== newHref) {
        canonical.href = newHref
      }
    }
    updateCanonical()

    // Update hreflang links based on current language route and page
    const updateHreflang = () => {
      // Remove existing hreflang links
      document.querySelectorAll('link[hreflang]').forEach(link => link.remove())

      // Legal pages: only show hreflang for languages with own content
      if (isLegalPage) {
        LANGUAGES_WITH_LEGAL.forEach(hreflang => {
          const link = document.createElement('link')
          link.rel = 'alternate'
          link.href = `${baseUrl}/${hreflang}${pagePath}`
          link.hreflang = hreflang
          document.head.appendChild(link)
        })

        // x-default points to Spanish legal page
        const xDefault = document.createElement('link')
        xDefault.rel = 'alternate'
        xDefault.href = `${baseUrl}/es${pagePath}`
        xDefault.hreflang = 'x-default'
        document.head.appendChild(xDefault)

        return
      }

      // Add new hreflang links dynamically
      const hreflangs = getHreflangLinks()

      hreflangs.forEach(({ href, hreflang }) => {
        const link = document.createElement('link')
        link.rel = 'alternate'
        link.href = href + pagePath
        link.hreflang = hreflang
        document.head.appendChild(link)
      })
    }
    updateHreflang()
  }, [lang, tFunc, isReady])
}
